import React, { useEffect, useState } from 'react';
import SideNavigationBar from '../../components/SideNavigationBar';
import { helperText, useMainCustomStyle } from '../../assets/theme/Theme';
import {
	Dialog,
	DialogContent,
	Grid,
	Typography,
	IconButton as MaterialIconButton,
	Button,
	Checkbox,
	CircularProgress,
	ButtonBase,
} from '@mui/material';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
	cancelApiRequest,
	generateInitialCareGoals,
	generateInitialCarePlan,
	getPlanTemplate,
	uploadInitialCarePlan,
} from '../../services/CarePlanServices';
import AddInitialCarePlan from './AddInitialCarePlan';
import GeneratedInitialPlan from './GeneratedInitialPlan';
import IconButton from '../../components/IconButton';
import { CloseIcon, PlanIcon } from '../../assets/icons/Icons';
import {
	getAccessToken,
	getElderHomeId,
	getUserId,
	getUserName,
	getUserType,
} from '../../Utils/AuthUtil';
import {
	getInitialCarePlan,
	getInitialPlanElderId,
} from '../../context/MainProvider';
import { useLocation, useNavigate } from 'react-router-dom';
import { PlanDocument } from '../../components/PDFDocument';
import { pdf } from '@react-pdf/renderer';
import { saveAs } from 'file-saver';
import UserLimitationPopup from '../../components/UserLimitationPopup';
import { USER_TYPES } from '../../Utils/Enums';
import {
	addInitialCarePlan,
	fetchElder,
	fetchExternalElder,
	getResidentIds,
	storeUpdatedElder,
} from '../../services/ElderService';
import SearchDropDown from '../../components/SearchDropDown';
import OptionPopup from '../../components/OptionPopup';
import ToastBanner from '../../components/ToastBanner';
import AlertBanner from '../../components/AlertBanner';
import { MAIN_THEME_COLOR } from '../../assets/theme/Colors';
import { sanitizeTitle } from '../../Utils/Helpers';

// eslint-disable-next-line no-undef
const central_db_host = process.env.REACT_APP_UMS_API_URL;

const INITIAL_ERROR_STATE = {
	status: false,
	msg: '',
};

function QuestionPopup({
	isModalOpen,
	onClose,
	popupTitle,
	data,
	selectedData,
	selectionError,
	onSelectItem,
	popupBtnState,
	onClickBtn,
	isLoading,
	onGoBackToMedicalConditionStep,
	isMedicalConditionStepComplete,
}) {
	return (
		<>
			<Dialog
				open={isModalOpen}
				fullWidth
				maxWidth="lg"
				onClose={onClose}
				scroll="body">
				<DialogContent style={{ padding: '0px 0px 20px 0px' }}>
					<Grid
						container
						sx={{ boxShadow: 3 }}
						padding={'10px'}
						flexDirection={'row'}
						justifyContent={'space-between'}
						alignItems={'center'}>
						<Typography fontWeight={'600'}>{popupTitle}</Typography>
						<MaterialIconButton
							style={{ padding: '0' }}
							onClick={onClose}>
							<CloseIcon />
						</MaterialIconButton>
					</Grid>
					<Grid container padding={'10px'}>
						{Object.keys(data).map((item, index) => {
							return (
								<Grid
									container
									key={index}
									padding={'5px'}
									flexDirection={'row'}
									alignItems={'center'}>
									<Checkbox
										style={{
											paddingLeft: '0px',
											paddingRight: '0px',
										}}
										size="small"
										color="success"
										disableRipple
										onChange={() => onSelectItem(item)}
										checked={selectedData.includes(item)}
									/>
									<Grid
										container
										width={'95%'}
										flexDirection={'row'}>
										<Typography
											variant="subtitle2"
											style={{
												color: '#545454',
												paddingLeft: '5px',
												fontWeight: '600',
											}}>
											{`${item} : `}
										</Typography>
										<Typography
											variant="subtitle2"
											sx={{
												whiteSpace: 'normal',
												wordBreak: 'break-word',
												color: '#949494',
												paddingLeft: '5px',
											}}>
											{data[item]}
										</Typography>
									</Grid>
								</Grid>
							);
						})}
					</Grid>
					{selectionError.status && (
						<Grid container paddingTop={'10px'} paddingX={'20px'}>
							<Typography style={helperText}>
								{selectionError.msg}
							</Typography>
						</Grid>
					)}
					<Grid
						container
						justifyContent={'space-between'}
						paddingTop={'10px'}
						alignItems={'center'}
						paddingX={'20px'}>
						{isMedicalConditionStepComplete ? (
							<Button
								onClick={onGoBackToMedicalConditionStep}
								sx={{
									border: '2px solid #28696D',
									paddingLeft: '15px',
									paddingRight: '15px',
									color: '#28696D',
									textTransform: 'none',
									marginRight: '10px',
									marginTop: '5px',
								}}>
								{'Back'}
							</Button>
						) : (
							<Grid />
						)}
						<Grid
							container
							maxWidth={'70%'}
							justifyContent={'flex-end'}
							paddingTop={'10px'}
							flexDirection={'row'}>
							{isLoading && (
								<Grid marginRight={'10px'} marginTop={'5px'}>
									<CircularProgress
										sx={{ color: '#28696D' }}
										size={'2rem'}
									/>
								</Grid>
							)}
							<Button
								onClick={onClose}
								sx={{
									paddingLeft: '15px',
									paddingRight: '15px',
									color: '#28696D',
									fontWeight: '600',
									textTransform: 'none',
									marginRight: '10px',
								}}>
								{'Cancel'}
							</Button>
							<IconButton
								text={popupBtnState}
								onClick={onClickBtn}
								disabled={isLoading}
							/>
						</Grid>
					</Grid>
				</DialogContent>
			</Dialog>
		</>
	);
}

function PlanTemplatesPopup({
	isModalOpen,
	templates,
	selected,
	onSelect,
	onSubmit,
	onClose,
}) {
	const Template = ({ title, isSelected, onSelectItem }) => {
		return (
			<ButtonBase
				onClick={onSelectItem}
				sx={{
					flex: 1,
					width: '100%',
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'center',
					alignItems: 'center',
					height: 100,
					border: `3px solid ${isSelected ? '#28696D' : '#e5e7eb'}`,
					padding: '10px 20px',
					borderRadius: '8px',
					'&:hover': {
						borderColor: '#28696D',
						borderStyle: 'solid',
					},
					backgroundColor: isSelected ? '#ebfaf8' : '#FFFFFF',
					transition: 'all 0.2s ease',
					boxShadow: isSelected ? 3 : 0,
				}}>
				<Typography
					sx={{
						fontSize: '16px',
						fontWeight: '500',
					}}>
					{title}
				</Typography>
			</ButtonBase>
		);
	};

	return (
		<Dialog
			open={isModalOpen}
			fullWidth
			maxWidth="lg"
			onClose={onClose}
			scroll="body">
			<DialogContent style={{ padding: '0px 0px 20px 0px' }}>
				<Grid
					container
					sx={{ boxShadow: 3 }}
					padding={'10px'}
					flexDirection={'row'}
					justifyContent={'space-between'}
					alignItems={'center'}>
					<Typography fontWeight={'600'}>
						{'Select care plan template'}
					</Typography>
					<MaterialIconButton
						style={{ padding: '0' }}
						onClick={onClose}>
						<CloseIcon />
					</MaterialIconButton>
				</Grid>
				<Grid container padding={'10px'}>
					<Grid container columnSpacing={3} rowSpacing={2}>
						{templates.map((plan, index) => {
							return (
								<Grid
									key={index}
									item
									xs={templates.length === 1 ? 4 : 12}
									md={4}>
									<Template
										title={plan.template_name}
										isSelected={
											selected === plan.template_id
										}
										onSelectItem={() =>
											onSelect(plan.template_id)
										}
									/>
								</Grid>
							);
						})}
					</Grid>
					<Grid
						container
						justifyContent={'flex-end'}
						alignItems={'center'}
						marginTop={'10px'}>
						<Button
							variant="contained"
							disabled={selected === ''}
							style={{
								width: '10%',
								backgroundColor:
									selected === '' ? '#FFFFFF' : '#28696D',
								paddingLeft: '15px',
								paddingRight: '15px',
								fontWeight: '600',
								textTransform: 'none',
								border: `1px solid ${selected === '' ? '#D9D9D9' : '#28696D'} `,
							}}
							onClick={onSubmit}>
							Next
						</Button>
					</Grid>
				</Grid>
			</DialogContent>
		</Dialog>
	);
}

function InitialCarePlanMain() {
	const mainDivStyle = useMainCustomStyle();
	const { state } = useLocation();
	const navigate = useNavigate();
	const token = getAccessToken();
	const homeId = getElderHomeId();
	const { initialCarePlan, storeInitialCarePlan } = getInitialCarePlan();
	const { initialPlanElderId, storeInitialPlanElderId } =
		getInitialPlanElderId();
	const [id, setId] = useState(null);
	const [txt, setTxt] = useState([]);
	const [fileData, setFileData] = useState([]);
	const [idError, setIdError] = useState(INITIAL_ERROR_STATE);
	const [txtError, setTxtError] = useState(INITIAL_ERROR_STATE);
	const [isLoading, setIsLoading] = useState(false);
	const [isPlanGenerated, setIsPlanGenerated] = useState(false);
	const [expandedAccordion, setExpandedAccordion] = useState(null);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isMultiFileModalOpen, setIsMultiFileModalOpen] = useState(false);
	const [popupTitle, setPopupTitle] = useState('');
	const [selectionData, setSelectionData] = useState({});
	const [selectedData, setSelectedData] = useState([]);
	const [selectedDataError, setSelectedDataError] =
		useState(INITIAL_ERROR_STATE);
	const [popupBtnState, setPopupBtnState] = useState('');
	const [generatedPlan, setGeneratedPlan] = useState({});
	const [sessionId, setSessionId] = useState('');
	const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);
	const [loadingMsg, setLoadingMsg] = useState('');
	const [isConnectApiEnabled, setIsConnectApiEnabled] = useState(true);
	const [medicalConditionData, setMedicalConditionData] = useState([]);
	const [selectedMedicalCondition, setSelectedMedicalCondition] = useState(
		[],
	);
	const [isMedicalConditionStepComplete, setIsMedicalConditionStepComplete] =
		useState(false);
	const [openLimitationModal, setOpenLimitationModal] = useState(false);
	const [remainingGeneration, setRemainingGeneration] = useState({
		count: 0,
		progress: 0,
		text: '',
	});
	const isDemoUser = getUserType() === USER_TYPES[2].key;
	const [residentIds, setResidentIds] = useState([]);
	const [residentFetchedIds, setResidentFetchedIds] = useState([]);
	const [onShowFileUpload, setOnShowFileUpload] = useState(false);
	const [isGenerationComplete, setIsGenerationComplete] = useState(false);
	const [resident, setResident] = useState({});
	const [residentOriginalData, setResidentOriginalData] = useState({});
	const [editedResident, setEditedResident] = useState({});
	const [editedResidentOriginalData, setEditedResidentOriginalData] =
		useState({});
	const [instruction, setInstruction] = useState('');
	const [showNoResidentBanner, setShowNoResidentBanner] = useState(false);
	const [showTemplates, setShowTemplates] = useState(false);
	const [planTemplates, setPlanTemplates] = useState([]);
	const [selectedTemplate, setSelectedTemplate] = useState('');
	const [isUserHasTemplates, setIsUserHasTemplates] = useState(false);
	const [isProfileEdited, setIsProfileEdited] = useState(false);
	const [noResidentMsg, setNoResidentMsg] = useState('No options');

	useEffect(() => {
		if (
			!state?.isUpdatingPageState &&
			initialCarePlan &&
			initialCarePlan.length > 0
		) {
			setGeneratedPlan(initialCarePlan);
			setId(initialPlanElderId);
			setIsPlanGenerated(true);
		}
	}, []);

	useEffect(() => {
		fetchResidentIds();
		fetchPlanTemplates();
	}, []);

	function onChangeId(value) {
		if (value.length > 10) {
			setIdError({
				status: true,
				msg: 'Only 10 characters allowed for Id',
			});
			return;
		} else {
			setIdError({ status: false, msg: '' });
		}

		const validId = value.replace(/[^a-z0-9-]/g, '');

		if (value !== validId) {
			setIdError({
				status: true,
				msg: 'Only lowercase letters, numbers, and dashes are allowed.',
			});
		} else {
			setIdError({ status: false, msg: '' });
		}

		setId(value);
	}

	function validateInputs() {
		let isValid = true;

		if (!id || id === '') {
			setIdError({ status: true, msg: 'Select resident id' });
			isValid = false;
		}

		if (txt.length === 0) {
			setTxtError({ status: true, msg: 'Upload the pdf file' });
			isValid = false;
		}

		return isValid;
	}

	async function onUploadPlan() {
		setIsLoading(true);
		setLoadingMsg('Generating health issues');
		const formData = new FormData();

		const selectedElder = residentFetchedIds.find(
			item => item.elderId === id.split(' ')[0] || item.name === id,
		);

		fileData.forEach(file => {
			formData.append(
				'uploaded_files',
				new Blob([file], { type: 'text/plain' }),
				file.name,
			);
		});
		if (selectedElder.isFromExternalApi) {
			formData.append('use_api', true);
			formData.append('elder_id', selectedElder.elderId);
		} else {
			formData.append('elder_id', selectedElder.id);
		}
		formData.append('resident_name', selectedElder.name);
		formData.append('input_text', instruction);
		formData.append('user_id', getUserId());
		formData.append('is_demo', isDemoUser);
		formData.append('base_url', central_db_host);
		formData.append('auth_token', token);
		formData.append('client_id', getElderHomeId());
		formData.append(
			'template_id',
			isUserHasTemplates && selectedTemplate ? selectedTemplate : null,
		);

		const responseData = await uploadInitialCarePlan(formData);

		if (!responseData.error) {
			setSelectionData(responseData.health_issues);
			setMedicalConditionData(responseData.health_issues);
			setSessionId(responseData.session_id);
			setPopupTitle(
				"Medical Conditions Identified from Resident's uploaded File (Unselect any conditions that you do not wish to include in the plan)",
			);
			setPopupBtnState('Next');
			setIsModalOpen(true);
			setSelectedData(Object.keys(responseData.health_issues));
		} else {
			if (responseData.data && responseData.data.limit_reached) {
				setOpenLimitationModal(true);
				setRemainingGeneration({
					count: 0,
					progress: 100 - (0 / 5) * 100,
					text: `You've generated 5 care plans and have 0 remaining.`,
				});
			} else {
				toast.error(responseData.message);
			}
		}
		setIsLoading(false);
		setIsMultiFileModalOpen(false);
		setLoadingMsg('');
	}

	async function onGenerateCareGoals() {
		if (selectedData && selectedData.length === 0) {
			setSelectedDataError({
				status: true,
				msg: 'Please select medical conditions',
			});
			return;
		}

		setIsLoadingQuestions(true);
		setLoadingMsg('Generating care goals');
		setSelectedMedicalCondition(selectedData);

		const body = {
			user_id: getUserId(),
			session_id: sessionId,
			selected_issues: selectedData,
			is_demo: isDemoUser,
			client_id: getElderHomeId(),
			template_id: isUserHasTemplates ? selectedTemplate : null,
		};

		const data = await generateInitialCareGoals(body);
		if (!data.error) {
			setSelectionData(data.care_goals);
			setPopupTitle(
				'The care categories to be included in the Initial care plan (Unselect any categories that are not required in the plan)',
			);
			setPopupBtnState('Create Plan');
			setIsModalOpen(true);
			setIsMedicalConditionStepComplete(true);
			setSelectedData(Object.keys(data.care_goals));
		} else {
			toast.error(data.message);
		}

		setIsLoadingQuestions(false);
		setLoadingMsg('');
	}

	function onFormatPlan(plan) {
		if (plan) {
			const formattedPlan = Object.keys(plan).map(item => {
				return {
					title: item,
					bodyContent: Object.keys(plan[item]).map(value => {
						return {
							label: sanitizeTitle(value),
							content: checkValueTypes(plan[item][value]),
						};
					}),
				};
			});

			return formattedPlan;
		} else {
			return [];
		}
	}

	async function onGenerateInitialCarePlan() {
		if (selectedData && selectedData.length === 0) {
			setSelectedDataError({
				status: true,
				msg: 'Please select sub plans',
			});
			return;
		}

		setIsLoadingQuestions(true);
		setLoadingMsg('Generating care plan');

		const body = {
			user_id: getUserId(),
			client_id: getElderHomeId(),
			session_id: sessionId,
			care_goals: selectedData,
			is_demo: isDemoUser,
			template_id: isUserHasTemplates ? selectedTemplate : null,
		};

		const updateInitialPlan = data => {
			if (data.status && data.status.startsWith('Processing')) {
				setLoadingMsg(data.status);
			}

			const sectionOrder = [
				'Current Situation',
				'Goals',
				'Action Description',
			];

			const formattedPlan =
				data.generated_section &&
				Object.keys(data.generated_section).map(item => {
					return {
						title: item,
						bodyContent: sectionOrder
							.filter(key => data.generated_section[item][key])
							.map(value => {
								return {
									label: sanitizeTitle(value),
									content: checkValueTypes(
										data.generated_section[item][value],
									),
								};
							}),
					};
				});
			if (formattedPlan) {
				setGeneratedPlan(formattedPlan);
				storeInitialCarePlan(formattedPlan);
			}

			if (data.done) {
				setIsGenerationComplete(true);
				setLoadingMsg('Initial care plan is created');
				const formattedPlanForDB = onFormatPlan(data.care_plan);
				storeInitialPlanData(formattedPlanForDB);
				setTimeout(() => {
					setIsLoadingQuestions(false);
					setLoadingMsg('');
					setIsGenerationComplete(false);
				}, 2000);
			}
		};

		await generateInitialCarePlan(body, updateInitialPlan);

		storeInitialPlanElderId(id);
		onCloseModal();
		onCloseFileUploadModal();
		setIsPlanGenerated(true);
	}

	async function storeInitialPlanData(data) {
		const selectedElder = residentFetchedIds.find(
			item => item.elderId === id.split(' ')[0] || item.name === id,
		);

		const planData = {
			elderHomeId: homeId,
			elderId: selectedElder.id,
			generatedUserId: getUserId(),
			generatedUserName: getUserName(),
			plan: data,
		};

		const formData = new FormData();
		formData.append(
			'plan',
			new Blob([JSON.stringify(planData)], {
				type: 'application/json',
			}),
		);

		fileData.forEach(file => {
			formData.append('file', file);
		});

		const response = await addInitialCarePlan(formData, token);

		if (response.error) {
			toast.error(response.message);
		}
	}

	function checkValueTypes(item) {
		if (
			!item ||
			(typeof item === 'string' && !item.trim()) ||
			(Array.isArray(item) && !item.length) ||
			(item &&
				typeof item === 'object' &&
				!Array.isArray(item) &&
				!Object.keys(item).length)
		) {
			return ['No data'];
		}

		if (
			Array.isArray(item) &&
			item.every(value => typeof value === 'object')
		) {
			const mergedArray = item.flatMap(obj =>
				Object.entries(obj).map(([key, value]) => `${key}:${value}`),
			);

			return mergedArray;
		} else if (typeof item === 'string') {
			return [item];
		} else {
			return item;
		}
	}

	function readTextFile(e) {
		const ext = e[0].path.split('.').pop().toLowerCase();

		if (!['txt', 'pdf', 'docx', 'xlsx'].includes(ext)) {
			setTxtError({ status: true, msg: 'Invalid file format.' });
			return;
		}

		if (e[0] && e[0].size === 0) {
			setTxtError({
				status: true,
				msg: 'The selected file is empty.',
			});
			return;
		}

		if (e[0] && e[0].size > 5242880) {
			setTxtError({
				status: true,
				msg: 'Maximum upload file size is 5MB.',
			});
			return;
		}

		setTxt([...txt, e[0]]);
		setTxtError({ status: false, msg: '' });
		setFileData([...fileData, e[0]]);
	}

	const handleAccordionExpansion = id => (_, isExpanded) => {
		setExpandedAccordion(isExpanded ? id : false);
	};

	function onClickPopupBtn() {
		if (popupBtnState === 'Next') {
			onGenerateCareGoals();
		} else {
			onGenerateInitialCarePlan();
		}
	}

	function onCloseModal() {
		setIsModalOpen(false);
		setSelectionData({});
		setPopupTitle('');
		setPopupBtnState('');
	}

	function onCloseFileUploadModal() {
		setIsMultiFileModalOpen(false);
		setTxt([]);
		setTxtError({ status: false, msg: '' });
		setFileData([]);
		setInstruction('');
		setSelectedTemplate('');
	}

	function onSelectItem(item) {
		setSelectedDataError(INITIAL_ERROR_STATE);
		if (selectedData.includes(item)) {
			const clone = selectedData.filter(value => value !== item);
			setSelectedData(clone);
		} else {
			setSelectedData([...selectedData, item]);
		}
	}

	function convertBulletStringToArray(str) {
		return str.split('\n').map(line => line.replace(/^•\s*/, ''));
	}

	function handlePlanChange(value, mainIndex, itemIndex) {
		const updatedItems = convertBulletStringToArray(value);
		const clone = generatedPlan.map((item, index) => {
			if (index === mainIndex) {
				return {
					...item,
					bodyContent: item.bodyContent.map((val, i) => {
						if (i === itemIndex) {
							return {
								...val,
								content: updatedItems,
								isPlanUpdated: true,
							};
						}

						return val;
					}),
				};
			}

			return item;
		});

		setGeneratedPlan(clone);
	}

	function onCreateNewPlan() {
		storeInitialCarePlan([]);
		setFileData([]);
		setTxt([]);
		setId(null);
		setIsPlanGenerated(false);
		setOnShowFileUpload(false);
		setGeneratedPlan({});
		setSelectedTemplate('');
	}

	async function onGeneratePdf() {
		if (isLoadingQuestions) {
			return;
		}

		const fileName = `SenzCare.AI_InitialCarePlan_${id}.pdf`;
		const elderId = onShowFileUpload ? id.split(' ')[0] : 'N/A';
		const name = onShowFileUpload ? id.split('(')[1].split(')')[0] : id;
		const blob = await pdf(
			<PlanDocument
				elderId={elderId}
				elderName={name}
				documentData={generatedPlan}
				title={'Initial Care Plan'}
			/>,
		).toBlob();
		saveAs(blob, fileName);
	}

	function onGoBackToMedicalConditionStep() {
		setSelectionData(medicalConditionData);
		setSelectedData(selectedMedicalCondition);
		setIsMedicalConditionStepComplete(false);
		setPopupTitle(
			"Medical Conditions Identified from Resident's uploaded File (Unselect any conditions that you do not wish to include in the plan)",
		);
		setPopupBtnState('Next');
	}

	async function fetchResidentIds() {
		const allResResponse = await getResidentIds(homeId, token);
		const response = await getResidentIds(homeId, token, 'initial');

		if (allResResponse.message === 'Success') {
			setShowNoResidentBanner(
				allResResponse.content.length > 0 ? false : true,
			);
			setNoResidentMsg(
				allResResponse.content.length > 0
					? 'All elders have care plans'
					: 'No options',
			);
		}

		if (response.message === 'Success') {
			const newIds = response.content.map(item =>
				item.isFromExternalApi
					? item.name
					: `${item.elderId} (${item.name})`,
			);

			setResidentFetchedIds(response.content);
			setResidentIds(newIds);
		} else {
			toast.error(response.message);
		}
	}

	async function fetchPlanTemplates() {
		const response = await getPlanTemplate(homeId);

		if (!response.error) {
			const isTempAvailable =
				response.templates.length > 0 ? true : false;
			if (isTempAvailable) {
				const defaultTemplate = {
					template_name: 'Default Template',
					template_id: null,
				};
				setPlanTemplates([defaultTemplate, ...response.templates]);
			}
			setIsUserHasTemplates(isTempAvailable);
		}
	}

	function onAddNewId(event) {
		setId(event.target.value);
		setIdError({ status: false, msg: '' });
	}

	function onRemoveFiles(content) {
		const clone = fileData.filter(value => value.name !== content.name);
		setTxt(clone);
		setFileData(clone);
	}

	function onOpenFileUploadModal() {
		if (!id || id === '') {
			setIdError({ status: true, msg: 'Select resident id' });
			return;
		}
		setIsMultiFileModalOpen(true);
	}

	function onResetLoaderAndCancelRequest() {
		cancelApiRequest();
		setIsLoading(false);
		setIsLoadingQuestions(false);
		setLoadingMsg('');
	}

	async function onFetchResidentProfile(value) {
		setIsLoadingQuestions(true);
		setLoadingMsg('Fetching resident profile');
		const selectedElder = residentFetchedIds.find(
			item => item.elderId === value.split(' ')[0] || item.name === value,
		);

		let reData;
		if (selectedElder.isFromExternalApi) {
			reData = await fetchExternalElder(selectedElder.id, token);
		} else {
			reData = await fetchElder(selectedElder.id, token);
		}

		if (!reData.error) {
			const { id, elderHomeId, createdDate, adminId, fileKey, ...rest } =
				reData;

			const filteredProfiles = Object.fromEntries(
				Object.entries(rest).filter(([_, value]) => {
					return (
						value != null &&
						value !== '' &&
						value !== 0 &&
						!(Array.isArray(value) && value.length === 0)
					);
				}),
			);

			const flattenedProfiles = flattenObject(filteredProfiles);

			setResident(flattenedProfiles);
			setEditedResident(reData);
			setResidentOriginalData(flattenedProfiles);
			setEditedResidentOriginalData(reData);
			setOnShowFileUpload(true);
			setIsLoadingQuestions(false);
			setLoadingMsg('');
		}
	}

	function flattenObject(obj, parentKey = '') {
		const flattened = {};

		for (const [key, value] of Object.entries(obj)) {
			const newKey = parentKey ? `${parentKey}.${key}` : key;

			if (value && typeof value === 'object' && !Array.isArray(value)) {
				Object.assign(flattened, flattenObject(value, newKey));
			} else if (Array.isArray(value)) {
				flattened[newKey] = value.join(', ');
			} else {
				flattened[newKey] = value;
			}
		}

		return flattened;
	}

	function onChangeSelection(e, value) {
		setId(value);
		setIdError({ status: false, msg: '' });
		setFileData([]);
		setInstruction('');
		onFetchResidentProfile(value);
	}

	function updateNested(obj, path, value) {
		const newPath = [...path];
		const keyName = newPath.pop();

		let newObj = { ...obj }; // full object (here it will be resident profile)
		let current = newObj;

		for (let i = 0; i < newPath.length; i++) {
			const pathKey = newPath[i];
			const nextKey = newPath[i + 1];

			//here checking the pathKey data is array, object or string
			if (Array.isArray(current[pathKey])) {
				current[pathKey] = [...current[pathKey]];
				current = current[pathKey];
			} else if (
				typeof current[pathKey] === 'object' &&
				current[pathKey] !== null
			) {
				current[pathKey] = { ...current[pathKey] };
				current = current[pathKey];
			} else {
				current[pathKey] = nextKey && /^\d+$/.test(nextKey) ? [] : {};
				current = current[pathKey];
			}
		}

		current[keyName] =
			typeof value === 'string' && keyName !== 'dateOfBirth'
				? [value]
				: value;

		return newObj;
	}

	function onChangeProfile(e, key) {
		setIsProfileEdited(true);
		const inputValue = e.target.value;
		setResident(prevState => ({
			...prevState,
			[key]: inputValue,
		}));

		setEditedResident(prevState => {
			let newValue = inputValue;

			// Force dateOfBirth to YYYY-MM-DD format
			if (key === 'dateOfBirth' || key.includes('dateOfBirth')) {
				newValue = formatDate(inputValue);
			}
			// Handle comma-separated values for other fields
			else if (inputValue.includes(',')) {
				newValue = inputValue
					.split(',')
					.map(item => item.trim())
					.filter(Boolean);
			}

			const path = key.split(/\.|\[/); // Splitting the key if it has . it will split else one key

			return updateNested(prevState, path, newValue);
		});
	}

	function formatDate(dateInput) {
		// Handle different input formats
		if (!dateInput) return '';

		// If already valid ISO format, return as-is
		const date = new Date(dateInput);
		if (!isNaN(date.getTime())) {
			return date.toISOString().split('T')[0]; // YYYY-MM-DD
		}

		// Try parsing common formats: DD/MM/YYYY, MM/DD/YYYY, DD-MM-YYYY
		const parts = dateInput.match(/(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{4})/);
		if (parts) {
			const day = parseInt(parts[1]);
			const month = parseInt(parts[2]) - 1; // JS months are 0-indexed
			const year = parseInt(parts[3]);

			const date = new Date(year, month, day);
			if (!isNaN(date.getTime())) {
				return date.toISOString().split('T')[0];
			}
		}

		return dateInput; // Fallback
	}

	async function onSaveChange() {
		const id = editedResident.elderId;
		const selectedElder = residentFetchedIds.find(
			item => item.elderId === id,
		);

		const route = `${selectedElder.isFromExternalApi ? 'external/elder' : 'elder'}/${selectedElder.id}`;

		const response = await storeUpdatedElder(token, editedResident, route);
		if (response.message === 'Success') {
			toast.success('Resident profile updated');

			const { id, elderHomeId, createdDate, adminId, fileKey, ...rest } =
				response.content;

			const filteredProfiles = Object.fromEntries(
				Object.entries(rest).filter(([_, value]) => {
					return (
						value != null &&
						value !== '' &&
						value !== 0 &&
						!(Array.isArray(value) && value.length === 0)
					);
				}),
			);
			const flattenedProfiles = flattenObject(filteredProfiles);
			setResidentOriginalData(flattenedProfiles);
			setIsProfileEdited(false);
		} else {
			toast.error(response.message);
		}
	}

	function onClearChange() {
		setResident(residentOriginalData);
		setEditedResident(editedResidentOriginalData);
		setIsProfileEdited(false);
	}

	function onRenderScreenContent() {
		if (isPlanGenerated) {
			return (
				<GeneratedInitialPlan
					expanded={expandedAccordion}
					handleExpansion={handleAccordionExpansion}
					planData={generatedPlan}
					onPlanChange={handlePlanChange}
					selectedElder={id}
					onCreateNewPlan={onCreateNewPlan}
					onExportPlan={onGeneratePdf}
					isLoading={isLoadingQuestions}
				/>
			);
		} else {
			return (
				<>
					<AddInitialCarePlan
						id={id}
						idError={idError}
						setId={onChangeId}
						isLoading={isLoading}
						onUploadPlan={onOpenFileUploadModal}
						isConnectApiEnabled={isConnectApiEnabled}
						residentIds={residentIds}
						onAddNewId={onAddNewId}
						elderIdDropDown={
							<Grid
								item
								xs={12}
								md={4}
								flexDirection={'column'}
								paddingRight={'10px'}
								paddingY={'5px'}>
								<SearchDropDown
									value={id}
									dropDownData={residentIds}
									onChange={onChangeSelection}
									placeholder={'Select Elder Id here...'}
									error={idError.status}
									disable={isLoadingQuestions || isLoading}
									placeholderTxt={noResidentMsg}
								/>
								<Typography style={helperText}>
									{idError.msg}
								</Typography>
							</Grid>
						}
						onShowFileUpload={onShowFileUpload}
						residentProfiles={resident}
						instruction={instruction}
						onChangeInstruction={value => setInstruction(value)}
						onGeneratePlan={() => {
							isUserHasTemplates
								? setShowTemplates(true)
								: onUploadPlan();
						}}
						files={fileData}
						onRemoveFiles={onRemoveFiles}
						readTextFile={readTextFile}
						txtError={txtError}
						onSaveChange={onSaveChange}
						onClearChange={onClearChange}
						onChangeProfile={onChangeProfile}
						isProfileEdited={isProfileEdited}
					/>
				</>
			);
		}
	}

	return (
		<>
			<SideNavigationBar />
			<div style={mainDivStyle}>
				<Grid
					container
					paddingLeft={{ xs: '0px', md: '20px', sm: '20px' }}>
					<Grid item paddingLeft="30px" paddingBottom={'20px'}>
						<Typography
							variant="h6"
							fontWeight={'600'}
							color="#28696D">
							Create a Plan
						</Typography>
					</Grid>
					{showNoResidentBanner && (
						<Grid
							container
							sx={{
								borderRadius: '5px',
								paddingLeft: '30px',
								paddingRight: '20px',
								paddingBottom: '10px',
							}}>
							<AlertBanner
								content={
									'No residents available. Add residents to get started.'
								}
								btnOneTxt={'Manual Onboarding'}
								btnTwoTxt={'Connect Api'}
								handleButtonOneClick={() =>
									navigate('/addResident')
								}
								handleButtonTwoClick={() =>
									navigate('/integration')
								}
								displayBtnOne={true}
								displayBtnTwo={true}
							/>
						</Grid>
					)}
					<Grid container marginLeft={'10px'}>
						{onRenderScreenContent()}
					</Grid>
				</Grid>
			</div>
			<QuestionPopup
				selectedData={selectedData}
				data={selectionData}
				isModalOpen={isModalOpen}
				popupTitle={popupTitle}
				popupBtnState={popupBtnState}
				onClose={() => {
					onCloseModal();
					onResetLoaderAndCancelRequest();
				}}
				onClickBtn={onClickPopupBtn}
				onSelectItem={onSelectItem}
				isLoading={false}
				onGoBackToMedicalConditionStep={onGoBackToMedicalConditionStep}
				isMedicalConditionStepComplete={isMedicalConditionStepComplete}
				selectionError={selectedDataError}
			/>
			{/* <OptionPopup
				isModalOpen={isMultiFileModalOpen}
				readTextFile={readTextFile}
				txt={txt}
				txtError={txtError}
				onClickBtn={onUploadPlan}
				isTypeUpload={'API'}
				popupTitle={'Upload files'}
				onClose={() => {
					onCloseFileUploadModal();
					onResetLoaderAndCancelRequest();
				}}
				popupBtnState={'Upload'}
				onRemoveFiles={onRemoveFiles}
				loading={false}
			/> */}
			<PlanTemplatesPopup
				isModalOpen={showTemplates}
				templates={planTemplates}
				selected={selectedTemplate}
				onSelect={value => setSelectedTemplate(value)}
				onSubmit={() => {
					onUploadPlan();
					setShowTemplates(false);
				}}
				onClose={() => {
					setShowTemplates(false);
					setSelectedTemplate('');
				}}
			/>
			<UserLimitationPopup
				isOpen={openLimitationModal}
				onClose={() => setOpenLimitationModal(false)}
				text={remainingGeneration.text}
				progress={remainingGeneration.progress}
			/>
			<ToastBanner
				open={isLoadingQuestions || isLoading}
				message={loadingMsg}
				icon={<PlanIcon color={'#FFFFFF'} />}
				isCompleted={isGenerationComplete}
			/>
		</>
	);
}

export default InitialCarePlanMain;
